import React, { useEffect, useId, useState, useRef } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import {
  LuQrCode,
  LuFileText,
  LuCircleCheck,
  LuCreditCard,
  LuCopy,
  LuX,
  LuInfo,
  LuWallet,
  LuRefreshCw,
  LuClock,
  LuExternalLink,
} from 'react-icons/lu';

import { formatters, financeiroAPI } from '../services/api';
import { useModalBlocker } from '../contexts/ModalGateContext';

// --- ESTILOS (Styled Components) ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 20px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${props => props.theme.colors.grayMedium};
  cursor: pointer;
  padding: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e2e8f0;
    color: ${props => props.theme.colors.grayDark};
    transform: rotate(90deg);
  }
`;

const BoletoInfo = styled.div`
  margin-bottom: 24px;
`;

const InfoSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #f8fafc;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.grayMedium};
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const BarcodeSection = styled.div`
  background: ${props => props.theme.colors.lightBlue};
  padding: 20px;
  border-radius: 8px;
`;

const BarcodeTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 12px;
  text-align: center;
`;

const BarcodeValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  text-align: center;
  background: ${props => props.theme.colors.white};
  padding: 12px;
  border-radius: 6px;
  border: 2px dashed ${props => props.theme.colors.primary};
  word-break: break-all;
  line-height: 1.4;
`;

const CopyButtonContainer = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const PaymentButtonsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    
    & > button {
      width: 100%;
    }
  }
`;

const CenteredActionRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 40px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 24px;
  flex-wrap: wrap;

  & > *:first-child {
    margin-right: auto;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    
    & > *:first-child {
      margin-right: 0;
      order: 1; /* Faz o botão de alternância aparecer por último no mobile */
    }
  }
`;

const ActionDivider = styled.div`
  height: 2px;
  background: ${props => props.theme.colors.lightBlue};
  border-radius: 999px;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const AttentionBox = styled.div`
  background: #fffbeb; /* amber-50 */
  border: 1px solid #fcd34d; /* amber-300 */
  color: #92400e; /* amber-800 */
  padding: 12px 14px;
  border-radius: 10px;
  margin-top: 14px;
  font-size: 13px;
  line-height: 1.35;
`;

const SelectionFootnote = styled.div`
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: ${props => props.theme.colors.grayMedium};
  line-height: 1.35;
`;

const CardPaymentPendingBox = styled.div`
  text-align: center;
  padding: 8px 0 4px;

  svg {
    color: ${props => props.theme.colors.secondary};
    margin-bottom: 12px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 16px;
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    color: ${props => props.theme.colors.grayDark};
    margin: 0 0 12px;
  }
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  background: ${props => {
    if (props.variant === 'secondary') return '#f1f5f9';
    if (props.variant === 'danger') return props.theme.colors.error;
    if (props.variant === 'pix') return '#32BCAD';
    return props.theme.colors.gradientPrimary;
  }};
  
  color: ${props => props.variant === 'secondary' ? '#64748b' : props.theme.colors.white};
  box-shadow: ${props => props.variant === 'secondary' ? 'none' : '0 10px 15px -3px rgba(30, 64, 175, 0.25)'};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary' ? '0 4px 6px -1px rgba(0, 0, 0, 0.05)' : '0 15px 20px -3px rgba(30, 64, 175, 0.35)'};
    background: ${props => props.variant === 'secondary' ? '#e2e8f0' : undefined};
    color: ${props => props.variant === 'secondary' ? '#1e293b' : undefined};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const InstallmentsSelect = styled.select`
  width: 100%;
  padding: 12px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: ${props => props.theme.colors.white};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'pago':
        return `background: #dcfce7; color: #166534; border: 1px solid #bbf7d0;`;
      case 'pendente':
        return `background: #fffbeb; color: #92400e; border: 1px solid #fef3c7;`;
      default:
        return `background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;`;
    }
  }};
`;

const PixContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const QRCodeWrapper = styled.div`
  background: transparent;
  padding: 16px;
  border-radius: 8px;
  display: inline-block;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
    text-align: center;
    padding: 40px 0;
`;

const PixLoadingSpinner = styled.div`
  border: 4px solid ${props => props.theme.colors.grayLight};
  border-top: 4px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto;
`;

const ClickableValue = styled(BarcodeValue)`
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8fafc;
  }
`;

const BarcodeInfoMessage = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  margin-top: 16px;
`;

const PaymentSuccessOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 12px;
  z-index: 10;
  padding: 20px;
  color: ${({ theme }) => theme.colors.success};

  svg {
    font-size: 48px;
    margin-bottom: 16px;
  }
`;

const AsaasLinkCard = styled.div`
  background: #f0fdf4;
  border: 1.5px solid #86efac;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
`;

const AsaasLinkCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 700;
  color: #166534;
`;

const AsaasLinkUrl = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: ${props => props.theme.colors.grayDark};
  background: #ffffff;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 10px 12px;
  word-break: break-all;
  margin-bottom: 12px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f8fafc; }
`;

const AsaasLinkActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BoletoModal = ({ boleto, isOpen, onClose }) => {
  const theme = useTheme();
  const boletoGateId = useId();
  useModalBlocker(boletoGateId, Boolean(isOpen && boleto));
  const [loadingSegundaVia, setLoadingSegundaVia] = useState(false);
  const [segundaVia, setSegundaVia] = useState(null);

  const [viewMode, setViewMode] = useState('loading');
  const [pixData, setPixData] = useState(null);
  const [initialPixLoadFailed, setInitialPixLoadFailed] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [loadingCardPayment, setLoadingCardPayment] = useState(false);
  const [cardOptionsLoading, setCardOptionsLoading] = useState(false);
  const [cardOptions, setCardOptions] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credito'); // credito | debito
  const [selectedInstallments, setSelectedInstallments] = useState(1);
  const [showInstallmentsChooser, setShowInstallmentsChooser] = useState(false);
  const [cardPaymentLinkOpened, setCardPaymentLinkOpened] = useState(false);
  const [asaasValidationLoading, setAsaasValidationLoading] = useState(false);
  const [existingAsaasData, setExistingAsaasData] = useState(null);
  const [generatedPaymentUrl, setGeneratedPaymentUrl] = useState(null);
  const [asaasCobrancaPaga, setAsaasCobrancaPaga] = useState(false);
  const [asaasValidationError, setAsaasValidationError] = useState(false);

  const [showQRCode, setShowQRCode] = useState(false);

  const loadedRef = useRef(null);

  useEffect(() => {
    const loadInitialPaymentMethod = async () => {
      const currentId = boleto?.originalData?.idBoleto || boleto?.idBoleto || boleto?.originalData?.SEQ || boleto?.seq;

      if (!isOpen || !boleto || loadedRef.current === currentId) return;

      loadedRef.current = currentId;

      setViewMode('loading');
      setPixData(null);
      setSegundaVia(null);
      setLoadingSegundaVia(false);
      setInitialPixLoadFailed(false);
      setIsPaid(false);
      setCardOptions(null);
      setSelectedPaymentMethod('credito');
      setSelectedInstallments(1);
      setShowInstallmentsChooser(false);
      setCardPaymentLinkOpened(false);
      setAsaasValidationLoading(false);
      setExistingAsaasData(null);
      setGeneratedPaymentUrl(null);
      setAsaasCobrancaPaga(false);
      setAsaasValidationError(false);

      // Caso 1: PIX já existe na base
      if (boleto.statusPix === 'PENDENTE' && boleto.pixCopiaECola && boleto.pixTXID) {
        setPixData({ qrCodePix: boleto.pixCopiaECola, txid: boleto.pixTXID });
        setViewMode('pix');
      } else {
        // Caso 2: PIX não existe ou , precisa ser gerado e validado
        if (boleto.statusPix === null || boleto.statusPix === 'CANCELADO' || boleto.statusPix === 'VENCIDO') {
          generateAndValidatePix();
        }
      }
    };

    const generateAndValidatePix = async () => {
      const id = boleto.originalData?.idBoleto || boleto.idBoleto || boleto.originalData?.SEQ || boleto.seq;
      let generatedPixData = null;

      // 1. Tenta gerar o PIX
      try {
        const response = await financeiroAPI.getPix(id);
        const pixPayload = response.data.data;
        const pixCode = pixPayload?.pixCopiaECola || pixPayload?.qrCodePix;

        if (response.data.success && pixCode && pixPayload.txid) {
          generatedPixData = { qrCodePix: pixCode, txid: pixPayload.txid };
        } else {
          throw new Error('API de geração de PIX não retornou dados válidos.');
        }
      } catch (error) {
        console.error('Falha ao gerar PIX:', error);
        toast.error('Erro ao gerar o PIX. Exibindo boleto.');
        setInitialPixLoadFailed(true);
        setViewMode('boleto');
        return;
      }

      // 2. Se gerou, aguarda 1s e começa a validar
      await new Promise(resolve => setTimeout(resolve, 1000));

      let validationAttempts = 0;
      const maxAttempts = 3;

      const validationInterval = setInterval(async () => {
        validationAttempts++;
        try {
          const statusResponse = await financeiroAPI.checkPixStatus(id, generatedPixData.txid);
          // Se a API de status retornar sucesso e um status (ex: PENDENTE), consideramos válido
          if (statusResponse.data && statusResponse.data.status) {
            clearInterval(validationInterval);
            setPixData(generatedPixData); // Define os dados do PIX para renderizar
            setViewMode('pix'); // Mostra o QR Code
          } else {
            throw new Error('Status do PIX ainda não disponível.');
          }
        } catch (error) {
          console.warn(`Tentativa ${validationAttempts} de validar o PIX falhou.`);
          if (validationAttempts >= maxAttempts) {
            clearInterval(validationInterval);
            toast.error('Não foi possível validar o PIX gerado. Exibindo boleto.');
            setInitialPixLoadFailed(true);
            setViewMode('boleto');
          }
        }
      }, 2000); // Tenta a cada 2 segundos
    };

    loadInitialPaymentMethod();
  }, [isOpen, boleto]);

  // Efeito para verificação do status do PIX em tempo real
  useEffect(() => {
    // O polling só deve começar quando o PIX for exibido na tela (viewMode === 'pix')
    // e os dados do PIX (com txid) estiverem definidos.
    const currentTxid = pixData?.txid;

    if (viewMode !== 'pix' || !isOpen || isPaid || !currentTxid) {
      return; // Não faz nada se não estiver na tela do PIX ou se já estiver pago
    }

    let pollingInterval;

    // Inicia a verificação após 10 segundos
    const initialTimeout = setTimeout(() => {
      const checkStatus = async () => {
        try {
          const id = boleto.originalData?.idBoleto || boleto.idBoleto || boleto.originalData?.SEQ || boleto.seq;
          const response = await financeiroAPI.checkPixStatus(id, currentTxid);

          if (response.data && (response.data.status === 'CONCLUIDA' || response.data.status === 'PAGO')) {
            toast.success('Pagamento confirmado!');
            setIsPaid(true);
            clearInterval(pollingInterval); // Para a verificação

            // Recarrega a página após 10 segundos
            setTimeout(() => {
              window.location.reload();
            }, 10000);
          }
        } catch (error) {
          console.warn('Falha ao verificar status do PIX, tentando novamente...', error);
        }
      };

      // Inicia o polling a cada 5 segundos
      pollingInterval = setInterval(checkStatus, 5000);
    }, 10000); // Atraso inicial de 10 segundos

    // Função de limpeza para interromper a verificação
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(pollingInterval);
    };
  }, [viewMode, isOpen, isPaid, pixData, boleto]);

  // Valida se já existe cobrança Asaas ativa ao entrar na view de cartão
  useEffect(() => {
    if (viewMode !== 'card' || !isOpen || !boleto) return;
    if (existingAsaasData !== null || asaasValidationLoading) return;

    const id = boleto.originalData?.idBoleto || boleto.idBoleto || boleto.originalData?.SEQ || boleto.seq;
    if (!id) return;

    const validate = async () => {
      setAsaasValidationLoading(true);
      try {
        const resp = await financeiroAPI.validateAsaasLink(id);
        const data = resp?.data;
        if (data?.geraCobranca === true) {
          // API autoriza gerar nova cobrança
          setExistingAsaasData(false);
        } else if (data?.cobrancaPaga === true) {
          // Cobrança já quitada — bloqueia nova geração
          setAsaasCobrancaPaga(true);
          setExistingAsaasData(false);
        } else if (data?.url) {
          // Existe link ativo — exibe opções de copiar/abrir
          setExistingAsaasData(data);
        } else {
          // Qualquer outro caso sem autorização explícita — bloqueia
          setAsaasValidationError(true);
          setExistingAsaasData(false);
        }
      } catch {
        setAsaasValidationError(true);
        setExistingAsaasData(false);
      } finally {
        setAsaasValidationLoading(false);
      }
    };

    validate();
  }, [viewMode, isOpen, boleto]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !boleto) return null;

  const getBoletoId = () => {
    return boleto.originalData?.idBoleto || boleto.idBoleto || boleto.originalData?.SEQ || boleto.seq;
  };

  const handleGenerateSegundaVia = async () => {
    try {
      setLoadingSegundaVia(true);
      const id = boleto.originalData?.idBoleto || boleto.idBoleto || boleto.originalData?.SEQ || boleto.seq;
      const response = await financeiroAPI.getSegundaVia(id);

      if (response.data.success) {
        setSegundaVia(response.data.data);
        toast.success('Segunda via gerada com sucesso!');
      } else {
        throw new Error('Erro ao gerar segunda via');
      }
    } catch (error) {
      console.error('Erro ao gerar segunda via:', error);
      toast.error('Erro ao gerar segunda via. Tente novamente.');
    } finally {
      setLoadingSegundaVia(false);
    }
  };

  const handleCopyBarcode = () => {
    const barcode = segundaVia?.LINHADIGITAVEL || boleto.linhaDigitavel || boleto.codigoBarras;
    if (barcode) {
      navigator.clipboard.writeText(barcode);
      toast.success('Código do boleto copiado!');
    }
  };

  const handleCopyPixCode = () => {
    if (pixData?.qrCodePix) {
      navigator.clipboard.writeText(pixData.qrCodePix);
      toast.success('Código PIX (Copia e Cola) copiado!');
    }
  };

  const openCardPaymentLink = (paymentUrl) => {
    setGeneratedPaymentUrl(paymentUrl);
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    setCardPaymentLinkOpened(true);
    setShowInstallmentsChooser(false);
  };

  const handleRedirectToCardPayment = async () => {
    try {
      const id = getBoletoId();

      if (!id) {
        toast.error('Não foi possível identificar o boleto para pagamento.');
        return;
      }

      setCardOptionsLoading(true);
      const optionsResp = await financeiroAPI.getCardPaymentOptions(id);
      const optionsData = optionsResp?.data?.data;

      const creditMax = Number(optionsData?.metodos?.find(m => m.metodo === 'credito')?.maxParcelas || 1);

      // Se não há opção de parcelamento (ou falha), mantém o fluxo atual (1x)
      if (!optionsData?.opcoes?.credito?.length || creditMax <= 1) {
        setLoadingCardPayment(true);
        const response = await financeiroAPI.getAsaasPaymentUrl(id, { metodo: 'credito', parcelas: 1 });
        const paymentUrl = response?.data?.url;

        if (!paymentUrl) {
          toast.error('Não foi possível obter o link de pagamento com cartão.');
          return;
        }

        openCardPaymentLink(paymentUrl);
        return;
      }

      setCardOptions(optionsData);
      setSelectedPaymentMethod('credito');
      setSelectedInstallments(1);
      setShowInstallmentsChooser(true);
    } catch (error) {
      console.error('Erro ao iniciar pagamento com cartão:', error);
      toast.error('Erro ao iniciar pagamento com cartão. Tente novamente.');
    } finally {
      setCardOptionsLoading(false);
      setLoadingCardPayment(false);
    }
  };

  const handleConfirmCardPayment = async () => {
    try {
      const id = getBoletoId();

      if (!id) {
        toast.error('Não foi possível identificar o boleto para pagamento.');
        return;
      }

      setLoadingCardPayment(true);
      const metodo = selectedPaymentMethod === 'debito' ? 'debito' : 'credito';
      const parcelas = metodo === 'debito' ? 1 : Number(selectedInstallments || 1);

      const response = await financeiroAPI.getAsaasPaymentUrl(id, { metodo, parcelas });
      const paymentUrl = response?.data?.url;

      if (!paymentUrl) {
        toast.error('Não foi possível obter o link de pagamento com cartão.');
        return;
      }

      openCardPaymentLink(paymentUrl);
    } catch (error) {
      console.error('Erro ao confirmar pagamento com cartão:', error);
      toast.error('Erro ao iniciar pagamento com cartão. Tente novamente.');
    } finally {
      setLoadingCardPayment(false);
    }
  };

  const renderLoadingView = () => (
    <>
      <ModalHeader>
        <ModalTitle>Carregando Pagamento</ModalTitle>
        <CloseButton onClick={onClose}><LuX /></CloseButton>
      </ModalHeader>
      <LoadingContainer>
        <PixLoadingSpinner />
      </LoadingContainer>
    </>
  );

  const renderPixView = () => (
    <>
      <ModalHeader>
        <ModalTitle>Pagamento com PIX</ModalTitle>
        <CloseButton onClick={onClose}><LuX /></CloseButton>
      </ModalHeader>

      <PixContainer>
        <AnimatePresence>
          {showQRCode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ width: '100%', overflow: 'hidden', textAlign: 'center', marginBottom: '0px' }}
            >
              <QRCodeWrapper style={{ margin: '0 auto' }}>
                <QRCodeSVG
                  value={pixData.qrCodePix}
                  size={200}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"L"}
                  includeMargin={false}
                />
              </QRCodeWrapper>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '0px' }}>
                Escaneie para pagar em outro dispositivo.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ width: '100%' }}>
          <SectionTitle style={{ justifyContent: 'center', marginBottom: '12px' }}>
            PIX Copia e Cola
          </SectionTitle>
          <BarcodeSection style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '16px' }}>
            <ClickableValue
              onClick={handleCopyPixCode}
              style={{
                fontSize: '13px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'left',
                marginBottom: '16px'
              }}
            >
              {pixData.qrCodePix}
            </ClickableValue>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <ActionButton
                variant="secondary"
                onClick={() => setShowQRCode(!showQRCode)}
                style={{ flex: 1, minWidth: '140px', background: '#f1f5f9', color: '#1e293b' }}
              >
                <LuQrCode size={16} /> {showQRCode ? 'Ocultar QR' : 'Gerar QR'}
              </ActionButton>

              <ActionButton
                onClick={handleCopyPixCode}
                disabled={isPaid}
                style={{ flex: 1, minWidth: '140px' }}
              >
                <LuCopy size={16} /> Copiar PIX
              </ActionButton>
            </div>
          </BarcodeSection>
        </div>

        <div style={{
          width: '100%',
          textAlign: 'center',
          marginTop: '0px',
          padding: '8px 12px',
          borderRadius: '8px',
          background: '#f1f5f9',
          color: '#475569',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <LuClock size={14} /> Normalmente a confirmação ocorre em segundos. Em casos raros, até 24h.
        </div>
      </PixContainer>

      <ActionDivider />
      <ActionButtons>
        <PaymentButtonsGroup>
          <ActionButton variant="secondary" onClick={() => setViewMode('boleto')} disabled={isPaid}>
            <LuFileText /> Pagar com Boleto
          </ActionButton>
          {!isPaid && (
            <ActionButton onClick={() => setViewMode('card')} disabled={isPaid}>
              <LuCreditCard /> Pagar com Cartão
            </ActionButton>
          )}
        </PaymentButtonsGroup>
        <ActionButton variant="secondary" onClick={onClose} disabled={isPaid}>
          Fechar
        </ActionButton>
      </ActionButtons>
    </>
  );

  const renderBoletoView = () => (
    <>
      <ModalHeader>
        <ModalTitle>Detalhes do Boleto</ModalTitle>
        <CloseButton onClick={onClose}><LuX /></CloseButton>
      </ModalHeader>
      <BoletoInfo>
        <InfoSection>
          <SectionTitle><LuInfo size={18} /> Informações Gerais</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Mês/Ano</InfoLabel>
              <InfoValue>{boleto.mesAno}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Valor</InfoLabel>
              <InfoValue style={{ color: theme.colors.primary }}>{formatters.formatCurrency(boleto.valor)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Vencimento</InfoLabel>
              <InfoValue>{boleto.vencimento || formatters.formatDate(boleto.dataVencimento)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Status</InfoLabel>
              <InfoValue>
                <StatusBadge status={boleto.status}>{boleto.status}</StatusBadge>
              </InfoValue>
            </InfoItem>
          </InfoGrid>
        </InfoSection>

        {boleto.pagamento && boleto.pagamento !== '-' && (
          <InfoSection>
            <SectionTitle><LuWallet size={18} /> Informações de Pagamento</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Data do Pagamento</InfoLabel>
                <InfoValue>{boleto.pagamento}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Valor Pago</InfoLabel>
                <InfoValue>{formatters.formatCurrency(boleto.valor)}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </InfoSection>
        )}

        <div style={{ width: '100%', marginTop: '8px' }}>
          <SectionTitle style={{ marginBottom: '12px' }}>
            <LuFileText size={18} /> Linha Digitável
          </SectionTitle>
          <BarcodeSection style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '16px' }}>
            <ClickableValue 
              onClick={handleCopyBarcode} 
              style={{ 
                fontSize: '13px', 
                background: '#ffffff', 
                border: '1px solid #cbd5e1',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'left',
                marginBottom: '16px',
                fontFamily: 'monospace'
              }}
            >
              {segundaVia?.LINHADIGITAVEL || boleto.linhaDigitavel || 'Linha digitável não disponível'}
            </ClickableValue>
            <CopyButtonContainer style={{ marginTop: '0px', justifyContent: 'center' }}>
              <ActionButton 
                onClick={handleCopyBarcode} 
                disabled={isPaid}
                style={{ width: '100%', maxWidth: '280px' }}
              >
                <LuCopy size={16} /> Copiar Código de Barras
              </ActionButton>
            </CopyButtonContainer>
          </BarcodeSection>
        </div>
      </BoletoInfo>

      <ActionDivider />
      <ActionButtons>
        <PaymentButtonsGroup>
          {boleto.status !== 'Pago' && !initialPixLoadFailed && (
            <ActionButton variant="pix" onClick={() => setViewMode('pix')} disabled={isPaid}>
              <LuQrCode /> Pagar com PIX
            </ActionButton>
          )}
          {boleto.status !== 'Pago' && (
            <ActionButton onClick={() => setViewMode('card')} disabled={isPaid}>
              <LuCreditCard /> Pagar com Cartão
            </ActionButton>
          )}
          {boleto.status === 'Pendente' && (
            <ActionButton onClick={handleGenerateSegundaVia} disabled={loadingSegundaVia || isPaid}>
              {loadingSegundaVia ? 'Gerando...' : <><LuRefreshCw size={16} /> Gerar Segunda Via</>}
            </ActionButton>
          )}
        </PaymentButtonsGroup>
        <ActionButton variant="secondary" onClick={onClose} disabled={isPaid}>
          Fechar
        </ActionButton>
      </ActionButtons>
    </>
  );

  const handleCopyAsaasUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link de pagamento copiado!');
  };

  const renderAsaasLinkBox = (url, data) => (
    <div style={{ width: '100%' }}>
      <SectionTitle style={{ justifyContent: 'center', marginBottom: '12px' }}>
        <LuCreditCard size={18} /> Link de Pagamento
      </SectionTitle>

      <BarcodeSection style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '16px' }}>
        {/* {data && (
          <InfoGrid style={{ marginBottom: 14 }}>
            {data.valor_parcela && (
              <InfoItem>
                <InfoLabel>{data.instalments > 1 ? `Parcela (${data.instalments}x)` : 'Valor'}</InfoLabel>
                <InfoValue>{formatters.formatCurrency(data.valor_parcela)}</InfoValue>
              </InfoItem>
            )}
            {data.data_vencimento_parcela && (
              <InfoItem>
                <InfoLabel>Vencimento</InfoLabel>
                <InfoValue>{formatters.formatDate(data.data_vencimento_parcela)}</InfoValue>
              </InfoItem>
            )}
          </InfoGrid>
        )} */}

        <ClickableValue
          onClick={() => handleCopyAsaasUrl(url)}
          style={{
            fontSize: '13px',
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            padding: '16px',
            borderRadius: '12px',
            textAlign: 'left',
            marginBottom: '16px',
          }}
        >
          {url}
        </ClickableValue>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <ActionButton
            onClick={() => handleCopyAsaasUrl(url)}
            style={{ flex: 1, minWidth: '140px' }}
          >
            <LuCopy size={16} /> Copiar link
          </ActionButton>
          <ActionButton
            variant="secondary"
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            style={{ flex: 1, minWidth: '140px', background: '#f1f5f9', color: '#1e293b' }}
          >
            <LuExternalLink size={16} /> Abrir no navegador
          </ActionButton>
        </div>
      </BarcodeSection>

      <div style={{
        width: '100%',
        textAlign: 'center',
        marginTop: '12px',
        padding: '8px 12px',
        borderRadius: '8px',
        background: '#f1f5f9',
        color: '#475569',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
      }}>
        <LuClock size={14} /> A confirmação do pagamento pode levar alguns minutos para aparecer no portal.
      </div>
    </div>
  );

  const renderCardView = () => (
    <>
      <ModalHeader>
        <ModalTitle>Pagamento com Cartão</ModalTitle>
        <CloseButton onClick={onClose}><LuX /></CloseButton>
      </ModalHeader>

      {/* Estado: validando cobrança existente */}
      {asaasValidationLoading && (
        <LoadingContainer>
          <PixLoadingSpinner />
          <p style={{ marginTop: 16, fontSize: 14, color: '#64748b' }}>Verificando cobrança...</p>
        </LoadingContainer>
      )}

      {/* Estado: cobrança já foi paga */}
      {!asaasValidationLoading && asaasCobrancaPaga && (
        <InfoSection>
          <div style={{
            background: '#f0fdf4',
            border: '1.5px solid #86efac',
            borderRadius: 14,
            padding: '20px 20px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 10,
          }}>
            <LuCircleCheck size={36} style={{ color: '#16a34a' }} />
            <div style={{ fontWeight: 700, fontSize: 16, color: '#166534' }}>Cobrança já paga</div>
            <p style={{ fontSize: 13, color: '#166534', margin: 0, lineHeight: 1.5 }}>
              Identificamos que essa cobrança já foi paga via cartão. Em breve o registro aparecerá
              como pago na plataforma.
            </p>
          </div>
        </InfoSection>
      )}

      {/* Estado: erro na validação */}
      {!asaasValidationLoading && asaasValidationError && (
        <InfoSection>
          <div style={{
            background: '#fff7ed',
            border: '1.5px solid #fdba74',
            borderRadius: 14,
            padding: '20px 20px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 10,
          }}>
            <LuInfo size={36} style={{ color: '#c2410c' }} />
            <div style={{ fontWeight: 700, fontSize: 16, color: '#9a3412' }}>Falha ao verificar cobrança</div>
            <p style={{ fontSize: 13, color: '#9a3412', margin: 0, lineHeight: 1.5 }}>
              Não foi possível habilitar o pagamento via cartão no momento. Tente novamente mais tarde
              ou utilize PIX ou Boleto.
            </p>
          </div>
        </InfoSection>
      )}

      {/* Estado: link existente retornado pela API */}
      {!asaasValidationLoading && existingAsaasData && existingAsaasData !== false && (
        <InfoSection>
          {renderAsaasLinkBox(existingAsaasData.url, existingAsaasData)}
        </InfoSection>
      )}

      {/* Estado: link gerado agora (após clicar em gerar) */}
      {!asaasValidationLoading && cardPaymentLinkOpened && generatedPaymentUrl && (
        <InfoSection>
          {renderAsaasLinkBox(generatedPaymentUrl, null)}
          <CenteredActionRow style={{ marginTop: 16, marginBottom: 0 }}>
            <ActionButton onClick={() => window.location.reload()}>
              <LuRefreshCw size={16} /> Recarregar página
            </ActionButton>
          </CenteredActionRow>
        </InfoSection>
      )}

      {/* Estado: sem cobrança existente — fluxo normal */}
      {!asaasValidationLoading && existingAsaasData === false && !cardPaymentLinkOpened && !asaasCobrancaPaga && !asaasValidationError && (
        <>
          <InfoSection>
            <SectionTitle>
              <LuCreditCard /> Pagamento com Cartão de Crédito/Débito
            </SectionTitle>
            <p>Escolha a forma de pagamento e finalize em uma página segura.</p>
            {cardOptions?.valorTotal !== undefined && (
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Valor</InfoLabel>
                  <InfoValue>{formatters.formatCurrency(cardOptions.valorTotal)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Regras</InfoLabel>
                  <InfoValue>
                    {cardOptions?.regras?.minValorParcela
                      ? `Mín. ${formatters.formatCurrency(cardOptions.regras.minValorParcela)}/parcela • Até ${cardOptions?.regras?.maxParcelas || 10}x`
                      : `Até ${cardOptions?.regras?.maxParcelas || 10}x`}
                  </InfoValue>
                </InfoItem>
              </InfoGrid>
            )}
            <p>
              A confirmação do pagamento pode levar alguns minutos para aparecer no portal, podendo demorar até
              24 horas.
            </p>
          </InfoSection>

          <CenteredActionRow>
            {!showInstallmentsChooser ? (
              <ActionButton onClick={handleRedirectToCardPayment} disabled={cardOptionsLoading || loadingCardPayment || isPaid}>
                {(cardOptionsLoading || loadingCardPayment) ? 'Carregando opções...' : 'Continuar'}
              </ActionButton>
            ) : (
              <div style={{ width: '100%' }}>
                <InfoSection style={{ marginBottom: 0 }}>
                  <SectionTitle>🧾 Em quantas vezes?</SectionTitle>
                  <InfoGrid>
                    <InfoItem
                      role="button"
                      tabIndex={0}
                      onClick={() => { setSelectedPaymentMethod('debito'); setSelectedInstallments(1); }}
                      style={{
                        cursor: 'pointer',
                        border: selectedPaymentMethod === 'debito' ? '2px solid #3b82f6' : '2px solid transparent',
                        background: selectedPaymentMethod === 'debito' ? '#eff6ff' : undefined,
                      }}
                    >
                      <InfoLabel>Débito</InfoLabel>
                      <InfoValue>1x (à vista)</InfoValue>
                    </InfoItem>
                    <InfoItem
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPaymentMethod('credito')}
                      style={{
                        cursor: 'pointer',
                        border: selectedPaymentMethod === 'credito' ? '2px solid #3b82f6' : '2px solid transparent',
                        background: selectedPaymentMethod === 'credito' ? '#eff6ff' : undefined,
                      }}
                    >
                      <InfoLabel>Crédito</InfoLabel>
                      <InfoValue>Parcelado (até {cardOptions?.metodos?.find(m => m.metodo === 'credito')?.maxParcelas || 10}x)</InfoValue>
                    </InfoItem>
                  </InfoGrid>

                  {selectedPaymentMethod === 'credito' && (
                    <div style={{ marginTop: 16 }}>
                      <InfoLabel style={{ marginBottom: 8 }}>Parcelas</InfoLabel>
                      <InstallmentsSelect
                        value={selectedInstallments}
                        onChange={(e) => setSelectedInstallments(Number(e.target.value))}
                      >
                        {(cardOptions?.opcoes?.credito || []).map((opt) => (
                          <option key={opt.installments} value={opt.installments}>
                            {opt.label}
                          </option>
                        ))}
                      </InstallmentsSelect>
                    </div>
                  )}

                  <SelectionFootnote>
                    * Atenção: após gerar o link, não será possível alterar entre crédito/débito ou o número de parcelas.
                    Você ainda pode pagar via PIX ou Boleto usando os botões abaixo.
                  </SelectionFootnote>
                </InfoSection>

                <CenteredActionRow style={{ marginTop: 16, marginBottom: 0 }}>
                  <ActionButton onClick={handleConfirmCardPayment} disabled={loadingCardPayment || isPaid}>
                    {loadingCardPayment ? 'Gerando link...' : 'Gerar link de pagamento'}
                  </ActionButton>
                </CenteredActionRow>
              </div>
            )}
          </CenteredActionRow>
        </>
      )}

      <ActionDivider />
      <ActionButtons>
        <PaymentButtonsGroup>
          {!asaasValidationLoading && !isPaid && !initialPixLoadFailed && (
            <ActionButton variant="pix" onClick={() => setViewMode('pix')} disabled={isPaid}>
              <LuQrCode /> Pagar com PIX
            </ActionButton>
          )}
          {!asaasValidationLoading && (
            <ActionButton variant="secondary" onClick={() => setViewMode('boleto')} disabled={isPaid}>
              <LuFileText /> Pagar com Boleto
            </ActionButton>
          )}
        </PaymentButtonsGroup>
        <ActionButton variant="secondary" onClick={onClose} disabled={isPaid}>
          Fechar
        </ActionButton>
      </ActionButtons>
    </>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'pix':
        return renderPixView();
      case 'boleto':
        return renderBoletoView();
      case 'card':
        return renderCardView();
      case 'loading':
      default:
        return renderLoadingView();
    }
  };

  return (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {isPaid && (
          <PaymentSuccessOverlay>
            <LuCircleCheck />
            <h2>Pagamento Confirmado!</h2>
            <p>A página será atualizada em instantes...</p>
          </PaymentSuccessOverlay>
        )}
        {renderContent()}
      </ModalContent>
    </ModalOverlay>
  );
};

export default BoletoModal;